import { useState, useRef, useCallback, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Image } from "@/components/common/image";

interface ImageCropModalProps {
  open: boolean;
  imageUrl: string;
  onOpenChange: (open: boolean) => void;
  onCropComplete: (croppedImageBlob: Blob) => void;
}

interface ImageSize {
  width: number;
  height: number;
}

interface ImagePosition {
  x: number;
  y: number;
}

export function ImageCropModal({
  open,
  onOpenChange,
  imageUrl,
  onCropComplete,
}: ImageCropModalProps) {
  const { t } = useTranslation();
  const [imageSize, setImageSize] = useState<ImageSize>({
    width: 0,
    height: 0,
  });
  const [imagePosition, setImagePosition] = useState<ImagePosition>({
    x: 0,
    y: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // 选框尺寸固定为400x400
  const cropSize = 400;

  // 计算图像显示尺寸，最大不超过480x480
  const calculateImageSize = useCallback(() => {
    if (!imageRef.current) return;

    const img = imageRef.current;
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    if (width === 0 || height === 0) return;

    let displayWidth = width;
    let displayHeight = height;
    if (displayWidth < displayHeight) {
      displayWidth = 400;
      displayHeight = (400 * height) / width;
    } else {
      displayWidth = (400 * width) / height;
      displayHeight = 400;
    }
    setImageSize({ width: displayWidth, height: displayHeight });

    // 重置图像位置到中心
    setImagePosition({ x: 0, y: 0 });
  }, []);

  // 计算拖拽边界限制
  const calculateDragLimits = useCallback(() => {
    const maxX = Math.max(0, (imageSize.width - cropSize) / 2);
    const maxY = Math.max(0, (imageSize.height - cropSize) / 2);

    return {
      minX: -maxX,
      maxX: maxX,
      minY: -maxY,
      maxY: maxY,
    };
  }, [imageSize.width, imageSize.height, cropSize]);

  // 限制位置在边界内
  const constrainPosition = useCallback(
    (x: number, y: number) => {
      const limits = calculateDragLimits();

      return {
        x: Math.max(limits.minX, Math.min(limits.maxX, x)),
        y: Math.max(limits.minY, Math.min(limits.maxY, y)),
      };
    },
    [calculateDragLimits]
  );

  // 鼠标按下开始拖拽
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y,
      });
    },
    [imagePosition]
  );

  // 鼠标移动拖拽
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      const constrainedPosition = constrainPosition(newX, newY);
      setImagePosition(constrainedPosition);
    },
    [isDragging, dragStart, constrainPosition]
  );

  // 鼠标抬起结束拖拽
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 添加全局鼠标事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 图像加载完成后计算尺寸
  const handleImageLoad = useCallback(() => {
    calculateImageSize();
  }, [calculateImageSize]);

  // 取消按钮处理
  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // 完成裁剪处理
  const handleDone = useCallback(async () => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = imageRef.current;

    // 计算图像的最小边（原图尺寸）
    const minEdge = Math.min(img.naturalWidth, img.naturalHeight);

    // 设置画布尺寸为选框尺寸
    canvas.width = cropSize;
    canvas.height = cropSize;

    // 直接使用原图和显示图的比例关系
    const displayToNaturalScaleX = img.naturalWidth / img.width;
    const displayToNaturalScaleY = img.naturalHeight / img.height;

    // 选框在容器中心，固定位置（显示尺寸下）
    const cropCenterX = cropSize / 2;
    const cropCenterY = cropSize / 2;

    // 图像中心在容器中的位置（考虑拖拽偏移）
    const imageCenterX = cropSize / 2 + imagePosition.x;
    const imageCenterY = cropSize / 2 + imagePosition.y;

    // 计算选框相对于图像的偏移（显示尺寸下）
    const offsetX = cropCenterX - imageCenterX;
    const offsetY = cropCenterY - imageCenterY;

    // 转换为原图尺寸下的偏移
    const naturalOffsetX = offsetX * displayToNaturalScaleX;
    const naturalOffsetY = offsetY * displayToNaturalScaleY;

    // 计算在原图中的裁剪起始点（以最小边为裁剪尺寸）
    const cropStartX = img.naturalWidth / 2 + naturalOffsetX - minEdge / 2;
    const cropStartY = img.naturalHeight / 2 + naturalOffsetY - minEdge / 2;

    // 确保裁剪区域在图像范围内
    const clampedCropStartX = Math.max(
      0,
      Math.min(img.naturalWidth - minEdge, cropStartX)
    );
    const clampedCropStartY = Math.max(
      0,
      Math.min(img.naturalHeight - minEdge, cropStartY)
    );
    const clampedCropWidth = Math.min(
      minEdge,
      img.naturalWidth - clampedCropStartX
    );
    const clampedCropHeight = Math.min(
      minEdge,
      img.naturalHeight - clampedCropStartY
    );

    // 绘制裁剪后的图像
    ctx.drawImage(
      img,
      clampedCropStartX,
      clampedCropStartY,
      clampedCropWidth,
      clampedCropHeight, // 源图裁剪区域
      0,
      0,
      cropSize,
      cropSize // 目标画布区域，使用最小边尺寸
    );
    // 转换为高质量 Blob
    canvas.toBlob(
      (blob) => {
        blob && onCropComplete(blob);
        onOpenChange(false);
      },
      "image/png",
      1.0
    ); // 使用最高质量
  }, [
    imageRef,
    imageSize,
    imagePosition,
    cropSize,
    onCropComplete,
    onOpenChange,
  ]);

  // 重置状态
  const handleClose = useCallback(() => {
    setImageSize({ width: 0, height: 0 });
    setImagePosition({ x: 0, y: 0 });
    setIsDragging(false);
    onOpenChange(false);
  }, [onOpenChange]);

  // 监听弹窗打开状态，重置图像加载状态
  useEffect(() => {
    if (open) {
      setImageSize({ width: 0, height: 0 });
      setImagePosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed inset-0 z-50 outline-none">
          {/* 图像显示区域 - 固定尺寸400x400 */}
          <div
            ref={containerRef}
            className="cursor-move w-full h-full flex items-center justify-center"
          >
            {imageUrl && (
              <div
                style={{
                  transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                }}
                onMouseDown={handleMouseDown}
              >
                <Image
                  ref={imageRef}
                  src={imageUrl}
                  alt={t("image_crop_modal.crop_preview_alt")}
                  className={`select-none ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                  style={{
                    width: `${imageSize.width}px`,
                    height: `${imageSize.height}px`,
                  }}
                  draggable={false}
                  onLoad={handleImageLoad}
                />
              </div>
            )}

            {/* 选框组件 - 1:1比例，不可拖拽，带网格样式 */}
            <div className="absolute w-[400px] h-[400px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              {/* 选框边框 */}
              <div className="absolute inset-0 border-2 border-white shadow-lg">
                {/* 网格线 */}
                <div className="absolute inset-0">
                  {/* 垂直网格线 */}
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/50"></div>
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/50"></div>
                  {/* 水平网格线 */}
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white/50"></div>
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white/50"></div>
                </div>

                {/* 四个角的标记 */}
                <div className="absolute -top-1 -left-1 w-3 h-3 border-l-2 border-t-2 border-white"></div>
                <div className="absolute -top-1 -right-1 w-3 h-3 border-r-2 border-t-2 border-white"></div>
                <div className="absolute -bottom-1 -left-1 w-3 h-3 border-l-2 border-b-2 border-white"></div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 border-r-2 border-b-2 border-white"></div>
              </div>
            </div>
          </div>

          {/* 隐藏的画布用于裁剪 */}
          <canvas ref={canvasRef} className="hidden" />

          {/* 遮盖层 - 覆盖除选框外的所有区域 */}
          {/* 上方遮盖 */}
          <div className="z-50 fixed left-0 right-0 top-0 h-[calc((100%-400px)/2)] bg-black/50"></div>

          {/* 下方遮盖 */}
          <div className="z-50 fixed left-0 right-0 bottom-0 h-[calc((100%-400px)/2)] bg-black/50"></div>

          {/* 左侧遮盖 */}
          <div className="z-50 fixed left-0 top-[calc((100%-400px)/2)] w-[calc((100%-400px)/2)] h-[400px] bg-black/50"></div>

          {/* 右侧遮盖 */}
          <div className="z-50 fixed right-0 top-[calc((100%-400px)/2)] w-[calc((100%-400px)/2)] h-[400px] bg-black/50"></div>
          <div className="z-[100] fixed bottom-[calc((100%-400px)/2)] right-[calc((100%-400px)/2)] transform translate-y-[calc(100%+8px)] flex justify-end">
            <Button
              tabIndex={-1}
              onClick={handleCancel}
              className="min-w-20 border-none rounded-sm rounded-r-none bg-i-normal text-white hover:bg-i-normal/90 hover:text-white"
            >
              {t("image_crop_modal.cancel_button")}
            </Button>
            <Button
              tabIndex={-1}
              onClick={handleDone}
              className="min-w-20 border-none rounded-sm rounded-l-none bg-i-normal text-white hover:bg-i-normal/90 hover:text-white"
            >
              {t("image_crop_modal.done_button")}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
